import { DropdownMenuItem } from "@/components/ui/baseUi/dropdown-menu"
import { errorToastClassName, successToastClassName, appToast as toast } from "@/components/ui/baseUi/sonner"
import { Text } from "@/components/ui/baseUi/text"
import { cn } from "@/modules/lib/utils"
import type {
  ExerciseFormikType,
  ExerciseInputKey,
} from "@/modules/sessions/sessionForm/blocks/exerciseBlock/exerciseFormikTypes"
import { useSessionExerciseCopy } from "@/modules/sessions/stores/useSessionExerciseCopyStore"
import { type HURichTextInputRef } from "@/modules/ui/components/richText/huRichTextInput"
import { formatTextToRichText } from "@/modules/ui/components/richText/richTextUtils"
import type { SimpleFormik } from "@/typings/formik"
import { PasteIcon } from "@images/svgIcons/pasteIcon"
import { word } from "@modules/core/utils/i18n"
import { EllipsisMenu } from "@modules/ui/components/ellipsisMenu"
import { type RefObject } from "react"

export function ExerciseFormHeader({
  formik,
  updatedExerciseIndex,
  editedExercise,
  richTextRef,
  className,
  ...props
}: {
  formik: SimpleFormik<ExerciseFormikType, ExerciseInputKey>
  updatedExerciseIndex: number
  editedExercise?: ExerciseFormikType
  richTextRef: RefObject<HURichTextInputRef | null>
} & React.ComponentProps<"div">) {
  const exerciseToCopy = useSessionExerciseCopy()

  const onExercisePaste = () => {
    try {
      if (exerciseToCopy) {
        const newBlock: ExerciseFormikType = {
          category: exerciseToCopy.category,
          otherCategory: exerciseToCopy.otherCategory,
          chrono: exerciseToCopy.chrono,
          chronoAmrap: exerciseToCopy.chronoAmrap,
          chronoEmom: exerciseToCopy.chronoEmom,
          chronoForTime: exerciseToCopy.chronoForTime,
          chronoTabata: exerciseToCopy.chronoTabata,
          chronoType: exerciseToCopy.chrono?.type ?? "NONE",
          description: exerciseToCopy.description,
          durationInMin: exerciseToCopy.durationInMin,
          id: null,
          scoreSetConfigurations: exerciseToCopy.scoreSetConfigurations,
          scoringOption: exerciseToCopy.scoringOption,
          rpe: exerciseToCopy.rpe,
          requiredMedia: exerciseToCopy.requiredMedia,
          title: "",
          scoreType: exerciseToCopy.scoreType,
        }
        formik.setValues(newBlock)

        // Force update the rich text input with the new description
        if (exerciseToCopy.description) {
          richTextRef.current?.forceNewContent(formatTextToRichText(exerciseToCopy.description))
        }
      }

      toast.success(word("program.exercise.paste.success"), {
        classNames: successToastClassName(),
      })
    } catch {
      toast.error(word("program.exercise.paste.error"), {
        classNames: errorToastClassName(),
      })
    }
  }

  return (
    <div className={cn("flex flex-row items-center gap-2", className)} {...props}>
      <div className="flex max-w-[70%] flex-col gap-2">
        <Text fontStyle="LS" className="text-textDefault-body uppercase">
          {`(${updatedExerciseIndex + 1}) ${word("program.planning.create.exercise.block_plural", { count: updatedExerciseIndex })}`}
        </Text>

        <Text fontStyle="HS">
          {!!editedExercise
            ? word("program.planning.edit.exercise.title")
            : word("program.planning.create.exercise.title")}
        </Text>

        <Text fontStyle="BS" className="text-textDefault-caption">
          {word("program.planning.create.exercise.description")}
        </Text>
      </div>

      {exerciseToCopy && (
        <div className="flex flex-1 justify-end gap-2">
          <EllipsisMenu propsContent={{ side: "bottom" }}>
            <DropdownMenuItem key="paste" onClick={() => onExercisePaste()}>
              <PasteIcon color="currentColor" className="text-textDefault-heading" />
              {word("program.exercise.paste.label")}
            </DropdownMenuItem>
          </EllipsisMenu>
        </div>
      )}
    </div>
  )
}
